/**
 * A React functional component that uses the `useDropzone` hook from the `react-dropzone` library.
 * @returns None
 */
import React, { useEffect } from "react";
import { useDropzone } from "react-dropzone";

/**
 * A component for uploading files.
 * @param {Object} props - The props for the FileUpload component.
 * @param {Function} onDrop - The function to be called when a file is dropped.
 * @returns The FileUpload component.
 */
function FileUpload(props) {
  const { onDrop, maxSize, accept, triggerOpen, children } = props;

  /**
   * Hook that provides props and methods for implementing a dropzone functionality.
   * @param {object} options - The options for configuring the dropzone behavior.
   * @param {string} options.accept - The file types to accept for dropping.
   * @param {number} options.maxSize - The maximum file size allowed for dropping.
   * @param {function} options.onDrop - The callback function to be executed when a file is dropped.
   * @param {boolean} options.multiple - Whether to allow multiple files to be dropped at once.
   * @returns {object} An object containing the props and methods for implementing the dropzone.
   * - getRootProps: A function that returns the props to be applied to the dropzone container element
   */
  const { getRootProps, getInputProps, inputRef } = useDropzone({
    accept: accept,
    maxSize: maxSize,
    onDrop: onDrop,
    multiple: false,
  });

  /**
   * useEffect hook that sets the current value of the inputRef to the triggerOpen prop.
   * This effect will run whenever the component is rendered or re-rendered.
   * @returns None
   */
  useEffect(() => {
    triggerOpen.current = inputRef.current;
  },);

  return (
    /**
     * Renders a section container with a dropzone for file uploads.
     * @param {Object} props - The component props
     * @returns The JSX element representing the section container with the dropzone.
     */
    <section className="container">
      <div {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
        {children}
      </div>
    </section>
  );
}
export default FileUpload;